/**
 * VacationAccountModal.js
 * Modal for managing vacation account settings and adjustments
 * Handles both initialization of new years and editing existing accounts
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useVacation } from '../../contexts/VacationContext';
import { useTranslation } from 'react-i18next';
import { AdjustmentType, AdjustmentTypeLabels } from '../../models/VacationAccountModel';
import '../../styles/vacation-modal.css';

const VacationAccountModal = ({ 
  isOpen, 
  onClose, 
  staffId, 
  companyId,
  year,
  mode = 'manage' // 'initialize' or 'manage'
}) => {
  const { t } = useTranslation('staff');
  const {
    vacationAccount,
    accountLoading,
    createVacationAccount,
    initializeAccountWithCarryover,
    updateVacationAccountSettings,
    addVacationAdjustment,
    deleteVacationAdjustment,
    deleteVacationAccount
  } = useVacation();

  // Determine effective mode - if manage mode but no account exists, treat as initialize
  const effectiveMode = useMemo(() => {
    if (mode === 'manage' && !vacationAccount) {
      return 'initialize';
    }
    return mode;
  }, [mode, vacationAccount]);

  // Check if account is missing (for warning display)
  const accountMissing = mode === 'manage' && !vacationAccount;

  const previousYear = useMemo(() => parseInt(year) - 1, [year]);
  const currentYear = year;

  // Form state
  const [formData, setFormData] = useState({
    baseEntitlement: 0,
    carryOverLimit: 0,
    carryOverDeadline: '',
    autoCalculateCarryover: true
  });

  // Adjustment form state
  const [adjustmentForm, setAdjustmentForm] = useState({
    type: AdjustmentType.ADMIN_ADJUSTMENT,
    days: 0,
    note: ''
  });

  const [showAddAdjustment, setShowAddAdjustment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('settings'); // 'settings' or 'adjustments'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track which adjustment index is pending delete confirmation
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const [isDeletingAdjustment, setIsDeletingAdjustment] = useState(false);

  /**
   * Initialize form data when modal opens or account loads
   */
  useEffect(() => {
    if (isOpen) {
      if (effectiveMode === 'manage' && vacationAccount) {
        setFormData({
          baseEntitlement: vacationAccount.baseEntitlement || 0,
          carryOverLimit: vacationAccount.carryOverLimit || 0,
          carryOverDeadline: vacationAccount.carryOverDeadline 
            ? new Date(vacationAccount.carryOverDeadline).toISOString().split('T')[0]
            : `${year}-03-31`,
          autoCalculateCarryover: false
        });
      } else if (effectiveMode === 'initialize') {
        setFormData({
          baseEntitlement: 0,
          carryOverLimit: 0,
          carryOverDeadline: `${year}-03-31`,
          autoCalculateCarryover: true
        });
      }
      setError(null);
      setShowAddAdjustment(false);
      setActiveTab('settings');
      setShowDeleteConfirm(false);
      setPendingDeleteIndex(null);
    }
  }, [isOpen, effectiveMode, vacationAccount, year]);

  /**
   * Handle form input changes
   */
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  /**
   * Handle adjustment form input changes
   */
  const handleAdjustmentInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setAdjustmentForm(prev => ({
      ...prev,
      [name]: name === 'days' ? parseFloat(value) || 0 : value
    }));
  }, []);

  /**
   * Validate form data
   */
  const validateForm = useCallback(() => {
    if (formData.baseEntitlement < 0) {
      return t('vacationAccount.validation.baseEntitlementPositive');
    }
    if (formData.carryOverLimit < 0) {
      return t('vacationAccount.validation.carryoverLimitPositive');
    }
    if (!formData.carryOverDeadline) {
      return t('vacationAccount.validation.carryoverDeadlineRequired');
    }
    return null;
  }, [formData, t]);

  /**
   * Handle delete account
   */
  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await deleteVacationAccount(staffId, year);
      onClose(true);
    } catch (err) {
      console.error('Error deleting vacation account:', err);
      setError(err.message || t('vacationAccount.errorDeletingAccount'));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [staffId, year, deleteVacationAccount, onClose, t]);

  /**
   * Handle save (initialize or update)
   */
  const handleSave = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (effectiveMode === 'initialize') {
        if (formData.autoCalculateCarryover) {
          await initializeAccountWithCarryover(
            staffId,
            year,
            parseFloat(formData.baseEntitlement),
            parseFloat(formData.carryOverLimit)
          );
        } else {
          await createVacationAccount(staffId, year, {
            baseEntitlement: parseFloat(formData.baseEntitlement),
            carryOver: 0,
            carryOverLimit: parseFloat(formData.carryOverLimit),
            carryOverDeadline: new Date(formData.carryOverDeadline)
          });
        }
      } else {
        await updateVacationAccountSettings(staffId, year, {
          baseEntitlement: parseFloat(formData.baseEntitlement),
          carryOverLimit: parseFloat(formData.carryOverLimit),
          carryOverDeadline: new Date(formData.carryOverDeadline)
        });
      }

      onClose(true);
    } catch (err) {
      console.error('Error saving vacation account:', err);
      setError(err.message || t('vacationAccount.errorSavingAccount'));
    } finally {
      setSaving(false);
    }
  }, [
    effectiveMode,
    formData,
    staffId,
    year,
    validateForm,
    createVacationAccount,
    initializeAccountWithCarryover,
    updateVacationAccountSettings,
    onClose,
    t
  ]);

  /**
   * Handle add adjustment
   */
  const handleAddAdjustment = useCallback(async () => {
    if (adjustmentForm.days === 0) {
      setError(t('vacationAccount.validation.adjustmentDaysRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await addVacationAdjustment(staffId, year, adjustmentForm);
      
      setAdjustmentForm({
        type: AdjustmentType.ADMIN_ADJUSTMENT,
        days: 0,
        note: ''
      });
      setShowAddAdjustment(false);
    } catch (err) {
      console.error('Error adding adjustment:', err);
      setError(err.message || t('vacationAccount.errorAddingAdjustment'));
    } finally {
      setSaving(false);
    }
  }, [adjustmentForm, staffId, year, addVacationAdjustment, t]);

  /**
   * Handle delete adjustment - initiates confirmation for a specific index
   */
  const handleDeleteAdjustmentRequest = useCallback((index) => {
    setPendingDeleteIndex(index);
    setError(null);
  }, []);

  /**
   * Handle cancel adjustment delete confirmation
   */
  const handleCancelAdjustmentDelete = useCallback(() => {
    setPendingDeleteIndex(null);
  }, []);

  /**
   * Handle confirm adjustment delete - calls service and updates state
   */
  const handleConfirmAdjustmentDelete = useCallback(async () => {
    if (pendingDeleteIndex === null) return;

    setIsDeletingAdjustment(true);
    setError(null);

    try {
      await deleteVacationAdjustment(staffId, year, pendingDeleteIndex);
      setPendingDeleteIndex(null);
    } catch (err) {
      console.error('Error deleting adjustment:', err);
      setError(err.message || t('vacationAccount.errorDeletingAdjustment'));
    } finally {
      setIsDeletingAdjustment(false);
    }
  }, [pendingDeleteIndex, staffId, year, deleteVacationAdjustment, t]);

  /**
   * Get adjustment type display name
   */
  const getAdjustmentTypeName = useCallback((type) => {
    const key = `vacationAccount.adjustmentTypes.${type.toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : AdjustmentTypeLabels[type];
  }, [t]);

  /**
   * Format date for display
   */
  const formatDate = useCallback((date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  if (!isOpen) return null;

  const canDelete = mode === 'manage' && vacationAccount && !accountMissing;

  // Determine if any async operation is in progress
  const isBusy = saving || isDeleting || isDeletingAdjustment;

  return (
    <div className="modal-overlay" onClick={() => !isBusy && onClose(false)}>
      <div className="modal-container vacation-account-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-content">
            <h2>
              {effectiveMode === 'initialize' 
                ? t('vacationAccount.modal.title', { year })
                : t('vacationAccount.manageAccount')}
            </h2>
            <p className="modal-subtitle">
              {effectiveMode === 'initialize' && t('vacationAccount.initializeYear')}
            </p>
          </div>
          <button 
            className="close-btn" 
            onClick={() => onClose(false)} 
            disabled={isBusy} 
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Account Missing Warning */}
        {accountMissing && (
          <div className="warning-banner">
            <span className="warning-icon">⚠️</span>
            <span>{t('vacationAccount.accountDoesNotExist', { year })}</span>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Tabs (only show in manage mode with existing account) */}
        {mode === 'manage' && vacationAccount && (
          <div className="modal-tabs">
            <button
              className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
              disabled={isBusy}
            >
              {t('vacationAccount.modal.accountSettings')}
            </button>
            <button
              className={`tab-btn ${activeTab === 'adjustments' ? 'active' : ''}`}
              onClick={() => setActiveTab('adjustments')}
              disabled={isBusy}
            >
              {t('vacationAccount.adjustments')} 
              {vacationAccount?.adjustments?.length > 0 && (
                <span className="tab-badge">{vacationAccount.adjustments.length}</span>
              )}
            </button>
          </div>
        )}

        {/* Modal Body */}
        <div className="modal-body">

          {/* SETTINGS TAB */}
          {(effectiveMode === 'initialize' || activeTab === 'settings') && (
            <div className="settings-section">

              {/* Base Entitlement */}
              <div className="form-group">
                <label htmlFor="baseEntitlement">
                  {t('vacationAccount.baseEntitlement')}
                  <span className="required">*</span>
                </label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    id="baseEntitlement"
                    name="baseEntitlement"
                    value={formData.baseEntitlement}
                    onChange={handleInputChange}
                    min="0"
                    step="0.5"
                    disabled={isBusy}
                  />
                  <span className="unit">{t('vacationAccount.days')}</span>
                </div>
                <p className="field-hint">
                  {t('vacationAccount.baseEntitlementHint')}
                </p>
              </div>

              {/* Carryover Limit */}
              <div className="form-group">
                <label htmlFor="carryOverLimit">
                  {t('vacationAccount.carryoverLimit')}
                  <span className="required">*</span>
                </label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    id="carryOverLimit"
                    name="carryOverLimit"
                    value={formData.carryOverLimit}
                    onChange={handleInputChange}
                    min="0"
                    step="0.5"
                    disabled={isBusy}
                  />
                  <span className="unit">{t('vacationAccount.days')}</span>
                </div>
                <p className="field-hint">
                  {t('vacationAccount.carryoverLimitHint')}
                </p>
              </div>

              {/* Carryover Deadline */}
              <div className="form-group">
                <label htmlFor="carryOverDeadline">
                  {t('vacationAccount.carryoverDeadline', { previousYear, currentYear })}
                  <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="carryOverDeadline"
                  name="carryOverDeadline"
                  value={formData.carryOverDeadline}
                  onChange={handleInputChange}
                  disabled={isBusy}
                />
                <p className="field-hint">
                  {t('vacationAccount.carryoverDeadlineHint', { previousYear, currentYear })}
                </p>
              </div>

              {/* Auto Calculate Carryover (only for initialize mode) */}
              {effectiveMode === 'initialize' && (
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="autoCalculateCarryover"
                      checked={formData.autoCalculateCarryover}
                      onChange={handleInputChange}
                      disabled={isBusy}
                    />
                    <span>{t('vacationAccount.autoCalculateCarryover')}</span>
                  </label>
                  <p className="field-hint">
                    {t('vacationAccount.autoCalculateCarryoverHint')}
                  </p>
                </div>
              )}

              {/* Current Account Summary (only in manage mode with existing account) */}
              {mode === 'manage' && vacationAccount && (
                <div className="current-summary">
                  <h4>{t('vacationAccount.currentAccountSummary')}</h4>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="label">{t('vacationAccount.totalEntitlement')}:</span>
                      <span className="value">{vacationAccount.totalEntitlement} {t('vacationAccount.days')}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">{t('vacationAccount.usedDays')}:</span>
                      <span className="value">{vacationAccount.usedDays} {t('vacationAccount.days')}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">{t('vacationAccount.remainingDays')}:</span>
                      <span className="value highlight">{vacationAccount.remainingDays} {t('vacationAccount.days')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ADJUSTMENTS TAB */}
          {mode === 'manage' && vacationAccount && activeTab === 'adjustments' && (
            <div className="adjustments-section">

              {/* Add Adjustment Button / Form */}
              {!showAddAdjustment ? (
                <button 
                  className="btn-secondary add-adjustment-btn"
                  onClick={() => setShowAddAdjustment(true)}
                  disabled={isBusy}
                >
                  + {t('vacationAccount.addAdjustment')}
                </button>
              ) : (
                <div className="add-adjustment-form">
                  <h4>{t('vacationAccount.newAdjustment')}</h4>
                  
                  {/* Adjustment Type */}
                  <div className="form-group">
                    <label htmlFor="adjustmentType">{t('vacationAccount.adjustmentType')}</label>
                    <select
                      id="adjustmentType"
                      name="type"
                      value={adjustmentForm.type}
                      onChange={handleAdjustmentInputChange}
                      disabled={isBusy}
                    >
                      {Object.values(AdjustmentType).map(type => (
                        <option key={type} value={type}>
                          {getAdjustmentTypeName(type)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Days */}
                  <div className="form-group">
                    <label htmlFor="adjustmentDays">
                      {t('vacationAccount.adjustmentDays')}
                      <span className="required">*</span>
                    </label>
                    <div className="input-with-unit">
                      <input
                        type="number"
                        id="adjustmentDays"
                        name="days"
                        value={adjustmentForm.days}
                        onChange={handleAdjustmentInputChange}
                        step="0.5"
                        placeholder="e.g., +5 or -2"
                        disabled={isBusy}
                      />
                      <span className="unit">{t('vacationAccount.days')}</span>
                    </div>
                    <p className="field-hint">
                      {t('vacationAccount.adjustmentDaysHint')}
                    </p>
                  </div>

                  {/* Note */}
                  <div className="form-group">
                    <label htmlFor="adjustmentNote">{t('vacationAccount.adjustmentNote')}</label>
                    <textarea
                      id="adjustmentNote"
                      name="note"
                      value={adjustmentForm.note}
                      onChange={handleAdjustmentInputChange}
                      rows="3"
                      placeholder={t('vacationAccount.adjustmentNotePlaceholder')}
                      disabled={isBusy}
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="form-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setShowAddAdjustment(false);
                        setAdjustmentForm({
                          type: AdjustmentType.ADMIN_ADJUSTMENT,
                          days: 0,
                          note: ''
                        });
                      }}
                      disabled={isBusy}
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      className="btn-primary"
                      onClick={handleAddAdjustment}
                      disabled={isBusy || adjustmentForm.days === 0}
                    >
                      {saving ? t('buttons.saving') : t('vacationAccount.addAdjustment')}
                    </button>
                  </div>
                </div>
              )}

              {/* Adjustments History */}
              <div className="adjustments-history">
                <h4>{t('vacationAccount.adjustmentHistory')}</h4>
                
                {!vacationAccount?.adjustments || vacationAccount.adjustments.length === 0 ? (
                  <div className="empty-state">
                    <p>{t('vacationAccount.noAdjustments')}</p>
                  </div>
                ) : (
                  <div className="adjustments-list">
                    {vacationAccount.adjustments.map((adjustment, index) => (
                      <div key={index} className="adjustment-item">

                        {/* Inline delete confirmation for this row */}
                        {pendingDeleteIndex === index ? (
                          <div className="adjustment-delete-confirm">
                            <p className="adjustment-delete-confirm-text">
                              {t('vacationAccount.confirmDeleteAdjustment')}
                            </p>
                            <div className="adjustment-delete-confirm-actions">
                              <button
                                className="btn-secondary btn-sm"
                                onClick={handleCancelAdjustmentDelete}
                                disabled={isDeletingAdjustment}
                              >
                                {t('common:cancel')}
                              </button>
                              <button
                                className="btn-danger btn-sm"
                                onClick={handleConfirmAdjustmentDelete}
                                disabled={isDeletingAdjustment}
                              >
                                {isDeletingAdjustment
                                  ? t('common:deleting')
                                  : t('common:delete')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="adjustment-header">
                              <span className="adjustment-type">
                                {getAdjustmentTypeName(adjustment.type)}
                              </span>
                              <div className="adjustment-header-right">
                                <span className={`adjustment-days ${adjustment.days >= 0 ? 'positive' : 'negative'}`}>
                                  {adjustment.days >= 0 ? '+' : ''}{adjustment.days} {t('vacationAccount.days')}
                                </span>
                                <button
                                  className="btn-icon btn-icon-danger"
                                  onClick={() => handleDeleteAdjustmentRequest(index)}
                                  disabled={isBusy}
                                  aria-label={t('vacationAccount.deleteAdjustment')}
                                  title={t('vacationAccount.deleteAdjustment')}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                            {adjustment.note && (
                              <p className="adjustment-note">{adjustment.note}</p>
                            )}
                            <div className="adjustment-meta">
                              <span className="adjustment-date">
                                {formatDate(adjustment.createdAt)}
                              </span>
                            </div>
                          </>
                        )}

                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          {/* Left side - Delete account button */}
          <div className="footer-left">
            {canDelete && !showDeleteConfirm && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isBusy}
              >
                {t('vacationAccount.deleteAccount')}
              </button>
            )}
          </div>

          {/* Right side - Action buttons */}
          <div className="footer-right">
            {showDeleteConfirm ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? t('common:deleting') : t('vacationAccount.confirmDelete')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => onClose(false)}
                  disabled={isBusy}
                >
                  {t('common:cancel')}
                </button>
                {(effectiveMode === 'initialize' || activeTab === 'settings') && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isBusy || accountLoading}
                  >
                    {saving 
                      ? t('buttons.saving') 
                      : effectiveMode === 'initialize' 
                        ? t('vacationAccount.modal.initializeAccount')
                        : t('vacationAccount.modal.save')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VacationAccountModal;